import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, ChevronLeft, ChevronRight, Clock, DollarSign, MapPin, CalendarDays, PartyPopper } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { PriceBreakdown } from "@/components/PriceBreakdown";
import StripeSecurityNote from "@/components/StripeSecurityNote";

const STEPS = ["Service", "Size", "Add-ons", "Address", "Price", "Schedule", "Details", "Policies", "Review"];

function money(n) { return `$${Number(n).toFixed(2)}`; }
function todayPlus(days) {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export default function BookNow() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [addons, setAddons] = useState([]);
  const [settings, setSettings] = useState(null);
  const [step, setStep] = useState(0);

  const [serviceSlug, setServiceSlug] = useState(params.get("service") || "");
  const [tierId, setTierId] = useState(params.get("tier") || "");
  const [selAddons, setSelAddons] = useState({}); // slug -> qty
  const [address, setAddress] = useState("");
  const [quote, setQuote] = useState(null);
  const [date, setDate] = useState("");
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [time, setTime] = useState("");
  const [cust, setCust] = useState({ customer_name: "", customer_email: "", customer_phone: "", property_instructions: "", access_instructions: "", parking_info: "" });
  const [agreed, setAgreed] = useState(false);
  const [recurring, setRecurring] = useState("none");
  const [discountInput, setDiscountInput] = useState("");
  const [discount, setDiscount] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [payingDeposit, setPayingDeposit] = useState(false);

  const payDeposit = async () => {
    setPayingDeposit(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        booking_id: confirmed.booking_id, kind: "deposit", origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(apiErr(err)); setPayingDeposit(false); }
  };

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data.filter((s) => s.bookable))).catch(() => {});
    api.get("/addons").then((r) => setAddons(r.data)).catch(() => {});
    api.get("/settings/public").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const service = services.find((s) => s.slug === serviceSlug);
  const tier = service?.tiers.find((t) => t.id === tierId);
  const leadHours = settings?.lead_time_hours ?? 24;
  const minDate = useMemo(() => todayPlus(Math.ceil(leadHours / 24)), [leadHours]);

  const addonPayload = () => Object.entries(selAddons).filter(([, q]) => q > 0).map(([slug, qty]) => ({ slug, qty }));

  // fetch quote when reaching price step
  useEffect(() => {
    if (step >= 4 && serviceSlug && tierId) {
      api.post("/quote", { service_slug: serviceSlug, tier_id: tierId, addons: addonPayload() })
        .then((r) => setQuote(r.data)).catch(() => {});
    }
  }, [step, serviceSlug, tierId, JSON.stringify(selAddons)]);

  const loadSlots = (d) => {
    setDate(d); setTime(""); setSlots([]); setSlotsLoading(true);
    const addonStr = addonPayload().map((a) => `${a.slug}:${a.qty}`).join(",");
    api.get("/availability", { params: { date: d, service_slug: serviceSlug, tier_id: tierId, addons: addonStr } })
      .then((r) => setSlots(r.data.slots)).catch(() => setSlots([])).finally(() => setSlotsLoading(false));
  };

  const canNext = () => {
    if (step === 0) return !!serviceSlug;
    if (step === 1) return !!tierId;
    if (step === 3) return address.trim().length > 3;
    if (step === 5) return !!date && !!time;
    if (step === 6) return cust.customer_name && cust.customer_email && cust.customer_phone;
    if (step === 7) return agreed;
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings", {
        service_slug: serviceSlug, tier_id: tierId, addons: addonPayload(), address,
        date, start_time: time, ...cust, recurring, agreed,
        discount_code: discount?.code || undefined,
      });
      setConfirmed(data);
      toast.success("Booking confirmed!");
    } catch (err) {
      toast.error(apiErr(err));
      if (apiErr(err).includes("no longer available")) setStep(5);
    } finally { setSubmitting(false); }
  };

  const applyDiscount = async () => {
    if (!discountInput.trim()) return;
    try {
      const { data } = await api.post("/discounts/validate", {
        code: discountInput, subtotal: quote.subtotal, service_slug: serviceSlug, customer_email: cust.customer_email });
      if (data.valid) { setDiscount({ code: discountInput.toUpperCase(), amount: data.amount }); toast.success(data.message); }
      else { setDiscount(null); toast.error(data.message); }
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (confirmed) {
    return (
      <div className="max-w-xl mx-auto px-4 py-20 text-center">
        <div className="w-16 h-16 rounded-full bg-[#B87333]/15 text-[#B87333] flex items-center justify-center mx-auto"><PartyPopper size={30} /></div>
        <h1 className="mt-6 font-serif text-4xl text-[#2C2621]">You're booked!</h1>
        <p className="mt-3 text-[#635B52]">A confirmation has been emailed to {confirmed.customer_email}.</p>
        <div className="mt-8 bg-white rounded-2xl border border-[#E6DEC3] p-6 text-left shadow-[0_4px_20px_rgba(44,38,33,0.04)]" data-testid="booking-confirmation">
          <p className="text-xs uppercase tracking-[0.2em] text-[#B87333] font-semibold">Booking number</p>
          <p className="font-serif text-2xl text-[#2C2621]" data-testid="confirmation-number">{confirmed.booking_number}</p>
          <div className="mt-4 space-y-1 text-[#635B52] text-sm">
            <p><strong>Service:</strong> {confirmed.service_name} — {confirmed.tier_label}</p>
            <p><strong>When:</strong> {confirmed.date} at {confirmed.start_time} (CT)</p>
            <p><strong>Duration:</strong> {confirmed.duration_minutes} min</p>
            <p><strong>Status:</strong> {confirmed.status.replace("_", " ")}</p>
          </div>
          <div className="mt-3 border-t border-[#E6DEC3] pt-3"><PriceBreakdown data={confirmed} /></div>
          {confirmed.status === "pending_approval" && (
            <p className="mt-3 text-sm text-[#B87333]">Same-day requests require owner approval. We'll confirm shortly.</p>
          )}
          {!confirmed.deposit_paid && confirmed.deposit_amount > 0 && (
            <div className="mt-4 border-t border-[#E6DEC3] pt-4">
              <p className="text-sm text-[#635B52]">Secure your appointment with a 50% deposit now. The balance is due after your clean.</p>
              <button onClick={payDeposit} disabled={payingDeposit} className="mt-3 w-full bg-[#B87333] hover:bg-[#A06127] text-white font-medium rounded-full px-6 py-3 shadow-md transition-all disabled:opacity-60" data-testid="pay-deposit-btn">
                {payingDeposit ? "Redirecting…" : `Pay 50% deposit — $${confirmed.deposit_amount.toFixed(2)}`}
              </button>
              <StripeSecurityNote className="mt-3" />
            </div>
          )}
        </div>
        <div className="mt-6 flex gap-3 justify-center">
          <Link to="/portal" className="bg-[#B87333] text-white rounded-full px-6 py-3 font-medium" data-testid="conf-portal">View my bookings</Link>
          <Link to="/" className="border border-[#B87333]/40 rounded-full px-6 py-3 font-medium text-[#2C2621]">Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
      <h1 className="font-serif text-3xl sm:text-4xl text-[#2C2621]">Book your cleaning</h1>
      {/* Progress */}
      <div className="mt-6 flex items-center gap-1 overflow-x-auto pb-2" data-testid="booking-steps">
        {STEPS.map((s, i) => (
          <div key={s} className="flex items-center shrink-0">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold ${i < step ? "bg-[#B87333] text-white" : i === step ? "bg-[#2C2621] text-white" : "bg-[#E6DEC3] text-[#635B52]"}`}>
              {i < step ? <Check size={14} /> : i + 1}
            </div>
            {i < STEPS.length - 1 && <div className={`w-6 h-0.5 ${i < step ? "bg-[#B87333]" : "bg-[#E6DEC3]"}`} />}
          </div>
        ))}
      </div>
      <p className="mt-2 text-sm text-[#635B52]">Step {step + 1} of {STEPS.length}: <strong className="text-[#2C2621]">{STEPS[step]}</strong></p>

      <div className="mt-6 bg-white rounded-2xl border border-[#E6DEC3] p-6 sm:p-8 shadow-[0_4px_20px_rgba(44,38,33,0.04)]">
        {/* Step 0: Service */}
        {step === 0 && (
          <div className="space-y-3" data-testid="step-service">
            <p className="text-[#635B52]">Which service do you need?</p>
            {services.map((s) => (
              <button key={s.slug} onClick={() => { setServiceSlug(s.slug); if (s.slug !== serviceSlug) setTierId(""); }}
                className={`w-full text-left rounded-xl border p-4 transition-all ${serviceSlug === s.slug ? "border-[#B87333] bg-[#B87333]/5 ring-1 ring-[#B87333]/30" : "border-[#E6DEC3] hover:border-[#B87333]/40"}`}
                data-testid={`service-opt-${s.slug}`}>
                <p className="font-semibold text-[#2C2621]">{s.name}</p>
                <p className="text-sm text-[#635B52]">{s.short_description}</p>
              </button>
            ))}
            <p className="text-sm text-[#635B52] pt-2">Need a deep clean? <Link to="/get-estimate" className="text-[#B87333] font-medium">Request an estimate →</Link></p>
          </div>
        )}

        {/* Step 1: Size */}
        {step === 1 && service && (
          <div className="space-y-3" data-testid="step-size">
            <p className="text-[#635B52]">Choose your property size.</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {service.tiers.map((t) => (
                <button key={t.id} onClick={() => setTierId(t.id)}
                  className={`text-left rounded-xl border p-4 transition-all ${tierId === t.id ? "border-[#B87333] bg-[#B87333]/5 ring-1 ring-[#B87333]/30" : "border-[#E6DEC3] hover:border-[#B87333]/40"}`}
                  data-testid={`tier-opt-${t.id}`}>
                  <p className="font-semibold text-[#2C2621]">{t.label}</p>
                  <p className="text-sm text-[#B87333]">{money(t.price)} · {t.duration_minutes} min</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Step 2: Add-ons */}
        {step === 2 && (
          <div className="space-y-3" data-testid="step-addons">
            <p className="text-[#635B52]">Add optional extras (skip if none).</p>
            {addons.map((a) => {
              const qty = selAddons[a.slug] || 0;
              const isCustom = a.price_type === "custom";
              const isQty = a.price_type === "each" || a.price_type === "per_load";
              const priceLabel = isCustom ? "Custom estimate" : a.price_type === "starting" ? `From $${a.price}` : a.price_type === "per_load" ? `$${a.price}/load` : a.price_type === "each" ? `$${a.price} each` : `$${a.price}`;
              return (
                <div key={a.slug} className="flex items-center justify-between rounded-xl border border-[#E6DEC3] p-4" data-testid={`addon-row-${a.slug}`}>
                  <div>
                    <p className="font-medium text-[#2C2621]">{a.name}</p>
                    <p className="text-sm text-[#635B52]">{priceLabel}{a.duration_minutes > 0 ? ` · +${a.duration_minutes} min` : ""}</p>
                  </div>
                  {isQty ? (
                    <div className="flex items-center gap-2">
                      <button onClick={() => setSelAddons({ ...selAddons, [a.slug]: Math.max(0, qty - 1) })} className="w-8 h-8 rounded-full border border-[#E6DEC3] text-[#2C2621]" data-testid={`addon-dec-${a.slug}`}>−</button>
                      <span className="w-6 text-center" data-testid={`addon-qty-${a.slug}`}>{qty}</span>
                      <button onClick={() => setSelAddons({ ...selAddons, [a.slug]: qty + 1 })} className="w-8 h-8 rounded-full border border-[#E6DEC3] text-[#2C2621]" data-testid={`addon-inc-${a.slug}`}>+</button>
                    </div>
                  ) : (
                    <button onClick={() => setSelAddons({ ...selAddons, [a.slug]: qty ? 0 : 1 })}
                      className={`w-11 h-6 rounded-full relative transition-colors ${qty ? "bg-[#B87333]" : "bg-[#E6DEC3]"}`} data-testid={`addon-toggle-${a.slug}`}>
                      <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${qty ? "left-5" : "left-0.5"}`} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Step 3: Address */}
        {step === 3 && (
          <div className="space-y-3" data-testid="step-address">
            <label className="text-[#635B52]">Service address</label>
            <input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="123 Main St, Austin, TX 78701"
              className="w-full rounded-xl border border-[#E6DEC3] px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#B87333]/30" data-testid="input-address" />
          </div>
        )}

        {/* Step 4: Price */}
        {step === 4 && quote && (
          <div data-testid="step-price">
            <p className="text-[#635B52]">Here's your estimate:</p>
            <div className="mt-4 divide-y divide-[#E6DEC3]">
              {quote.lines.map((l, i) => (
                <div key={i} className="flex justify-between py-2.5 text-[#2C2621]">
                  <span>{l.label}{l.starting ? " (starting)" : ""}{l.custom ? " (custom quote)" : ""}</span>
                  <span className="font-medium">{l.custom ? "TBD" : money(l.price)}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 bg-[#F5F0E8] rounded-xl p-4">
              <div className="flex justify-between items-center mb-2">
                <p className="text-xs uppercase tracking-[0.2em] text-[#B87333] font-semibold">Your estimate</p>
                <p className="flex items-center gap-1 text-[#635B52] text-sm"><Clock size={15} /> {quote.duration_minutes} min</p>
              </div>
              <PriceBreakdown data={quote} />
            </div>
            {quote.custom_required && <p className="mt-3 text-sm text-[#B87333]">Some selected items require a custom estimate; the owner will confirm the final price.</p>}
            <p className="mt-3 text-xs text-[#635B52]">A 50% deposit is required to confirm your appointment; the remaining balance is due after your clean. Final price may adjust for unusual conditions.</p>
          </div>
        )}

        {/* Step 5: Schedule */}
        {step === 5 && (
          <div className="space-y-4" data-testid="step-schedule">
            <div>
              <label className="text-[#635B52] flex items-center gap-1"><CalendarDays size={16} /> Choose a date</label>
              <input type="date" min={minDate} value={date} onChange={(e) => loadSlots(e.target.value)}
                className="mt-1 w-full rounded-xl border border-[#E6DEC3] px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#B87333]/30" data-testid="input-date" />
              <p className="mt-1 text-xs text-[#635B52]">Minimum {leadHours} hours advance notice. All times are America/Chicago.</p>
            </div>
            {date && (
              <div>
                <p className="text-[#635B52] mb-2">Available start times</p>
                {slotsLoading ? <p className="text-[#635B52]">Loading…</p> : slots.length === 0 ? (
                  <p className="text-[#635B52] bg-[#F5F0E8] rounded-xl p-4" data-testid="no-slots">No available times on this date. Please choose another day.</p>
                ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="slots">
                    {slots.map((s) => (
                      <button key={s} onClick={() => setTime(s)}
                        className={`rounded-lg border py-2.5 text-sm font-medium transition-all ${time === s ? "border-[#B87333] bg-[#B87333] text-white" : "border-[#E6DEC3] text-[#2C2621] hover:border-[#B87333]/40"}`}
                        data-testid={`slot-${s}`}>{s}</button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Step 6: Details */}
        {step === 6 && (
          <div className="space-y-3" data-testid="step-details">
            {[
              { k: "customer_name", label: "Full name", req: true },
              { k: "customer_email", label: "Email", req: true, type: "email" },
              { k: "customer_phone", label: "Phone", req: true },
            ].map((f) => (
              <div key={f.k}>
                <label className="text-sm font-medium text-[#2C2621]">{f.label}</label>
                <input type={f.type || "text"} value={cust[f.k]} onChange={(e) => setCust({ ...cust, [f.k]: e.target.value })}
                  className="mt-1 w-full rounded-xl border border-[#E6DEC3] px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#B87333]/30" data-testid={`input-${f.k}`} />
              </div>
            ))}
            {[
              { k: "property_instructions", label: "Property instructions" },
              { k: "access_instructions", label: "Access instructions (gate codes, keys)" },
              { k: "parking_info", label: "Parking information" },
            ].map((f) => (
              <div key={f.k}>
                <label className="text-sm font-medium text-[#2C2621]">{f.label}</label>
                <textarea rows={2} value={cust[f.k]} onChange={(e) => setCust({ ...cust, [f.k]: e.target.value })}
                  className="mt-1 w-full rounded-xl border border-[#E6DEC3] px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#B87333]/30" data-testid={`input-${f.k}`} />
              </div>
            ))}
            {serviceSlug === "residential" && (
              <div className="bg-[#F5F0E8] rounded-xl p-4">
                <label className="text-sm font-medium text-[#2C2621]">Make this a recurring cleaning?</label>
                <p className="text-xs text-[#635B52] mb-2">We'll automatically schedule your next visit after each clean.</p>
                <select value={recurring} onChange={(e) => setRecurring(e.target.value)} className="w-full rounded-xl border border-[#E6DEC3] px-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-[#B87333]/30" data-testid="input-recurring">
                  <option value="none">One-time cleaning</option>
                  <option value="weekly">Weekly</option>
                  <option value="biweekly">Every 2 weeks</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>
            )}
          </div>
        )}

        {/* Step 7: Policies */}
        {step === 7 && settings && (
          <div className="space-y-4" data-testid="step-policies">
            <div className="max-h-56 overflow-y-auto bg-[#F5F0E8] rounded-xl p-4 text-sm text-[#635B52] space-y-3">
              <div><p className="font-semibold text-[#2C2621]">Service terms</p><p>{settings.terms}</p></div>
              <div><p className="font-semibold text-[#2C2621]">Cancellation policy</p><p>{settings.cancellation_policy}</p></div>
            </div>
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} className="mt-1 w-5 h-5 accent-[#B87333]" data-testid="agree-checkbox" />
              <span className="text-[#2C2621]">I agree to the service and cancellation policies.</span>
            </label>
          </div>
        )}

        {/* Step 8: Review */}
        {step === 8 && quote && (
          <div className="space-y-4" data-testid="step-review">
            <h3 className="font-serif text-2xl text-[#2C2621]">Review your booking</h3>
            <div className="space-y-2 text-[#635B52]">
              <p className="flex items-center gap-2"><Check size={16} className="text-[#B87333]" /> {quote.service_name} — {quote.tier_label}</p>
              <p className="flex items-center gap-2"><MapPin size={16} className="text-[#B87333]" /> {address}</p>
              <p className="flex items-center gap-2"><CalendarDays size={16} className="text-[#B87333]" /> {date} at {time} (CT)</p>
              <p className="flex items-center gap-2"><Clock size={16} className="text-[#B87333]" /> {quote.duration_minutes} min · {quote.cleaners || 1} cleaner{(quote.cleaners || 1) > 1 ? "s" : ""}</p>
              {serviceSlug === "residential" && recurring !== "none" && (
                <p className="flex items-center gap-2" data-testid="review-recurring"><CalendarDays size={16} className="text-[#B87333]" /> Recurring: {recurring === "biweekly" ? "every 2 weeks" : recurring}</p>
              )}
              <p className="text-sm">{cust.customer_name} · {cust.customer_email} · {cust.customer_phone}</p>
            </div>
            <div className="bg-[#F5F0E8] rounded-xl p-4"><PriceBreakdown data={quote} /></div>
            <div data-testid="discount-section">
              <label className="text-sm font-medium text-[#2C2621]">Discount code</label>
              <div className="mt-1 flex gap-2">
                <input value={discountInput} onChange={(e) => setDiscountInput(e.target.value.toUpperCase())} placeholder="Enter code" className="flex-1 rounded-xl border border-[#E6DEC3] px-4 py-2.5 text-sm" data-testid="discount-input" />
                <button type="button" onClick={applyDiscount} className="rounded-full px-5 py-2.5 text-sm font-medium border border-[#B87333] text-[#B87333]" data-testid="discount-apply">Apply</button>
              </div>
              {discount && <p className="mt-2 text-sm text-green-700" data-testid="discount-applied">Code {discount.code} applied — you save ${discount.amount.toFixed(2)}. Final total (before tax reduced) is shown on your confirmation.</p>}
            </div>
            <button onClick={submit} disabled={submitting} className="w-full bg-[#B87333] hover:bg-[#A06127] text-white font-medium rounded-full px-6 py-3.5 shadow-md transition-all disabled:opacity-60" data-testid="submit-booking">
              {submitting ? "Submitting…" : "Confirm booking"}
            </button>
          </div>
        )}
      </div>

      {/* Nav */}
      <div className="mt-5 flex justify-between">
        <button onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0}
          className="inline-flex items-center gap-1 text-[#635B52] hover:text-[#2C2621] font-medium disabled:opacity-40" data-testid="wizard-back">
          <ChevronLeft size={18} /> Back
        </button>
        {step < STEPS.length - 1 && (
          <button onClick={() => canNext() && setStep(step + 1)} disabled={!canNext()}
            className="inline-flex items-center gap-1 bg-[#2C2621] hover:bg-[#1E1A17] text-white font-medium rounded-full px-6 py-2.5 transition-all disabled:opacity-40" data-testid="wizard-next">
            {step === 2 && Object.values(selAddons).every((q) => !q) ? "Skip" : "Continue"} <ChevronRight size={18} />
          </button>
        )}
      </div>
    </div>
  );
}
